import React, { useState, useEffect } from 'react';
import { Form, Input, Checkbox, Button, Select } from 'antd';
import { PlusOutlined, DeleteOutlined, DownOutlined } from '@ant-design/icons';
import { getPublicReferences } from '../api/publicRequests';

const ContactsSection = () => {
  const [isApplicantResponsible, setIsApplicantResponsible] = useState(true);
  const [mobileCodes, setMobileCodes] = useState([]);
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    getPublicReferences().then(data => {
      if (data?.MobileCodes) {
        setMobileCodes(data.MobileCodes);
      }
      if (data?.PlantEmployee) {
        setPositions(data.PlantEmployee);
      }
    });
  }, []);

  const phoneValidationRule = {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      
      const cleanValue = value.replace(/\s/g, '');
      
      if (!cleanValue.startsWith('+38')) {
        return Promise.reject(new Error('Номер повинен починатися з +38'));
      }

      if (cleanValue.length < 6) return Promise.resolve();

      const operatorCode = cleanValue.substring(3, 6);
      const isValidCode = mobileCodes.some(m => m.Code === operatorCode);

      if (mobileCodes.length > 0 && !isValidCode) {
        return Promise.reject(new Error('Невірний код оператора'));
      }

      if (cleanValue.length !== 13) {
        return Promise.reject(new Error('Номер повинен містити 12 цифр (+38XXXXXXXXXX)'));
      }
      return Promise.resolve();
    }
  };

  const handlePhoneKeyPress = (e) => {
    const allowedChars = /^[0-9+]$/;
    if (!allowedChars.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className={`contacts-roles-grid ${isApplicantResponsible ? 'single-col' : ''}`}>

      {/* ── Особа, що оформлює ──────────────────────── */}
      <div className={`contact-role-card ${isApplicantResponsible ? 'full-width-card' : ''}`}>
        <div className="contact-role-header">
          <span className="contact-role-badge badge-applicant">Особа, що оформлює заявку</span>
        </div>

        <div className="form-grid" style={{ marginTop: 10 }}>
          <Form.Item
            name={['Contact', 'Name']}
            label="ПІБ"
            rules={[
              { required: true, message: 'Введіть ПІБ' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  return value.trim().split(/\s+/).length >= 2
                    ? Promise.resolve()
                    : Promise.reject(new Error("Введіть прізвище та ім'я"));
                }
              }
            ]}
          >
            <Input placeholder="Прізвище Ім'я По батькові" />
          </Form.Item>
          <Form.Item
            name={['Contact', 'Phone']}
            label="Телефон"
            rules={[{ required: true, message: 'Введіть телефон' }, phoneValidationRule]}
          >
            <Input 
              placeholder="+380XXXXXXXXX" 
              maxLength={13}
              onKeyPress={handlePhoneKeyPress}
            />
          </Form.Item>
          <Form.Item
            name={['Contact', 'Email']}
            label="Email"
            rules={[
              { required: true, message: 'Введіть email' },
              { type: 'email', message: 'Невірний формат email' }
            ]}
          >
            <Input placeholder="example@mail.com" />
          </Form.Item>
          <Form.Item
            name={['Contact', 'Job']}
            label="Посада"
            rules={[{ required: true, message: 'Оберіть посаду' }]}
          >
            <Select
              placeholder="Оберіть посаду зі списку"
              options={positions.map(pos => ({ label: pos.Name, value: pos.Id }))}
              suffixIcon={<DownOutlined />}
            />
          </Form.Item>
        </div>

        <div className="contact-also-responsible">
          <Checkbox
            checked={isApplicantResponsible}
            onChange={(e) => setIsApplicantResponsible(e.target.checked)}
          >
            Ця особа також є відповідальною 
          </Checkbox>
          <div className="contact-role-hint" style={{ marginTop: '4px', marginLeft: '24px' }}>Особа, яка погоджена з адміністрацією ТРЦ.</div>
        </div>
        
        {/* Приховане поле, щоб передати стан */}
        <Form.Item name="isApplicantResponsible" initialValue={true} hidden>
          <Input />
        </Form.Item>
      </div>

      {/* ── Відповідальний (з'являється при знятті галочки) ── */}
      {!isApplicantResponsible && (
        <div className="contact-role-card contact-role-animate">
          <div className="contact-role-header">
            <span className="contact-role-badge badge-responsible">Відповідальна особа орендаря</span>
          </div>
          <div className="form-grid" style={{ marginTop: 10 }}>
            <Form.Item
              name="SuTenantResonsible"
              label="ПІБ"
              rules={[{ required: !isApplicantResponsible, message: 'Введіть ПІБ' }]}
            >
              <Input placeholder="Прізвище Ім'я По батькові" />
            </Form.Item>
            <Form.Item
              name="SuTelNumberTenant"
              label="Телефон"
              rules={[{ required: !isApplicantResponsible, message: 'Введіть телефон' }, phoneValidationRule]}
            >
              <Input 
                placeholder="+380XXXXXXXXX" 
                maxLength={13}
                onKeyPress={handlePhoneKeyPress}
              />
            </Form.Item>
          </div>
        </div>
      )}

      {/* ── Всі інші присутні (Виконавці) ────────── */}
      <div className="contact-role-card full-width-card">
        <div className="contact-role-header">
          <span className="contact-role-badge badge-others">Виконавці (всі інші присутні)</span>
          <span className="contact-role-hint">якщо є</span>
        </div>
        <Form.List name="RespNightWorkDetails">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key} className="contacts-list-item contact-role-animate">
                  <Form.Item
                    {...restField}
                    name={[name, 'name']}
                    style={{ flex: 1, marginBottom: 0 }}
                  >
                    <Input placeholder="Прізвище, Ім'я" />
                  </Form.Item>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                    style={{ flexShrink: 0, marginTop: 4 }}
                  />
                </div>
              ))}
              <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Додати особу
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </div>

    </div>
  );
};

export default ContactsSection;
